import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar
} from '@mui/material';
import { Add, Delete, Edit } from '@mui/icons-material';
import { getLevels, addLevel, updateLevel, deleteLevel } from '../../services/adminService';

function LevelManagement() {
  const [levels, setLevels] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [editingLevel, setEditingLevel] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    requiredScore: 0,
    order: 1
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const loadLevels = useCallback(async () => {
    try {
      const data = await getLevels();
      const sortedLevels = data.sort((a, b) => a.order - b.order);
      setLevels(sortedLevels);
    } catch (error) {
      console.error('Seviyeler yüklenirken hata:', error);
      showSnackbar('Seviyeler yüklenirken hata oluştu', 'error');
    }
  }, []);

  useEffect(() => {
    loadLevels();
  }, [loadLevels]);

  const handleOpenDialog = (level = null) => {
    if (level) {
      setEditingLevel(level);
      setFormData({
        name: level.name,
        description: level.description,
        requiredScore: level.requiredScore,
        order: level.order
      });
    } else {
      const maxOrder = Math.max(...levels.map(l => l.order), 0);
      setEditingLevel(null);
      setFormData({
        name: '',
        description: '',
        requiredScore: 0,
        order: maxOrder + 1
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingLevel(null);
    setFormData({ name: '', description: '', requiredScore: 0, order: 1 });
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      if (!formData.name.trim()) {
        setError('Seviye adı zorunludur');
        return;
      }

      const levelData = {
        ...formData,
        name: formData.name.trim(),
        requiredScore: parseInt(formData.requiredScore, 10) || 0,
        order: parseInt(formData.order, 10) || 1
      };

      if (editingLevel) {
        await updateLevel(editingLevel.id, levelData);
        showSnackbar('Seviye başarıyla güncellendi');
      } else {
        await addLevel(levelData);
        showSnackbar('Seviye başarıyla eklendi');
      }

      await loadLevels();
      handleCloseDialog();
    } catch (error) {
      console.error('Seviye kaydedilirken hata:', error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteLevel = async (levelId) => {
    if (window.confirm('Bu seviyeyi silmek istediğinize emin misiniz?')) {
      try {
        await deleteLevel(levelId);
        showSnackbar('Seviye başarıyla silindi');
        await loadLevels();
      } catch (error) {
        console.error('Seviye silinirken hata:', error);
        showSnackbar('Seviye silinirken hata oluştu', 'error');
      }
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5">Seviye Yönetimi</Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
          sx={{ background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)' }}
        >
          Yeni Seviye
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Sıra</TableCell>
              <TableCell>Seviye Adı</TableCell>
              <TableCell>Açıklama</TableCell>
              <TableCell>Gerekli Puan</TableCell>
              <TableCell>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {levels.map((level) => (
              <TableRow key={level.id}>
                <TableCell>{level.order}</TableCell>
                <TableCell>{level.name}</TableCell>
                <TableCell>{level.description}</TableCell>
                <TableCell>{level.requiredScore}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpenDialog(level)}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDeleteLevel(level.id)} color="error">
                    <Delete />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLevel ? 'Seviye Düzenle' : 'Yeni Seviye Ekle'}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2, mt: 2 }}>
              {error}
            </Alert>
          )}
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Seviye Adı"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="Açıklama"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              type="number"
              label="Gerekli Puan"
              value={formData.requiredScore}
              onChange={(e) => setFormData({ ...formData, requiredScore: e.target.value })}
              margin="normal"
            />
            <TextField
              fullWidth
              type="number"
              label="Sıra"
              value={formData.order}
              onChange={(e) => setFormData({ ...formData, order: e.target.value })}
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>İptal</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={loading}
            sx={{ background: 'linear-gradient(45deg, #00bf0d 30%, #4cf13d 90%)' }}
          >
            {loading ? 'Kaydediliyor...' : (editingLevel ? 'Güncelle' : 'Ekle')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </Box>
  );
}

export default LevelManagement; 